/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Pressable } from '@components/atoms'
import { Footer, Seo } from '@components/systems'
import { useNavigate } from 'react-router-dom'
import { grey } from '@mui/material/colors'

/**
 * 존재하지 않는 경로.
 *
 * 예전에는 `path: '*'` 가 홈으로 리다이렉트했다. CloudFront 가 없는 경로까지
 * 200 + index.html 로 응답하기 때문에(8번 fallback), 리다이렉트만으로는
 * 크롤러 입장에서 "홈과 같은 내용의 페이지가 무한히 있는" 상태가 된다.
 * 여기서 noindex 를 명시해 색인을 막는다.
 */
const NotFoundPage = () => {
	const navigate = useNavigate()

	const linkList = [
		{ label: 'Shop', path: '/shop' },
		{ label: 'Support', path: '/support' },
		{ label: 'Manuals', path: '/manuals' },
		{ label: 'Contact', path: '/contact' },
	]

	return (
		<View css={style}>
			<Seo
				title="Page Not Found | Judahs"
				description="The page you were looking for does not exist."
				noindex={true}
			/>

			<View className="notFoundOuterView">
				<View className="notFoundInnerView">
					<Text category="h1" className="notFoundCode">
						404
					</Text>
					<Text as="h1" category="h4" className="notFoundTitle">
						This page does not exist.
					</Text>
					<Text category="p1" className="notFoundBody">
						The link may be out of date, or the address may have a typo.
					</Text>

					<View className="notFoundLinkView">
						<Pressable className="notFoundHomeLink" onClick={() => navigate('/')}>
							<Text category="s1">Go to home</Text>
						</Pressable>

						<View className="notFoundSubLinkView">
							{linkList.map(aLink => (
								<Pressable
									key={aLink.path}
									className="notFoundSubLink"
									onClick={() => navigate(aLink.path)}
								>
									<Text category="p2">{aLink.label}</Text>
								</Pressable>
							))}
						</View>
					</View>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default NotFoundPage

const style = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	minHeight: '100%',

	'.notFoundOuterView': {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		width: '100%',
		padding: '6rem 1.5rem 8rem',
	},

	'.notFoundInnerView': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',
		maxWidth: '32rem',
	},

	'.notFoundCode': {
		margin: 0,
		fontSize: '5rem',
		lineHeight: 1,
		color: grey[300],
		letterSpacing: '0.05em',
	},

	'.notFoundTitle': {
		marginTop: '1.5rem',
		marginBottom: 0,
		color: grey[900],
	},

	'.notFoundBody': {
		marginTop: '0.75rem',
		color: grey[600],
	},

	'.notFoundLinkView': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: '1.5rem',
		marginTop: '2.5rem',
	},

	'.notFoundHomeLink': {
		padding: '0.65rem 1.75rem',
		borderRadius: '999px',
		backgroundColor: grey[900],
		cursor: 'pointer',

		p: {
			margin: 0,
			color: '#fff',
		},

		'&:hover': {
			backgroundColor: grey[800],
		},
	},

	'.notFoundSubLinkView': {
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'center',
		gap: '1.25rem',
	},

	'.notFoundSubLink': {
		cursor: 'pointer',

		p: {
			margin: 0,
			color: grey[600],
			textDecoration: 'underline',
		},

		'&:hover p': {
			color: grey[900],
		},
	},
})
